import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import {ExclamationTriangleIcon} from '@heroicons/react/24/outline';

import AnimationCircle from './AnimationCircle';
import { CardHeader } from './ui/Card';
import {
  fetchActiveUsers,
  fetchMonthlyStats,
  fetchMonthlyStatsPrevious,
  selectActiveUsers,
  selectMonthlyStatsData,
  selectMonthlyStatsLoading,
  selectMonthlyStatsPreviousData,
} from '../slices/reportsSlice';
import {selectBlurSensitive} from '../slices/preferencesSlice';

const formatScore = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toFixed(2);
};

const formatDelta = (value) => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  if (Math.abs(num) < 0.01) return '0.00';
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(2)}`;
};

export default function MonthlyLeaderboard({
  resourceId,
  activeUserId,
  isAdmin = false,
  track,
  year,
  month,
  horizon,
  onForecasterClick,
}) {
  const dispatch = useDispatch();

  const monthlyStatsData = useSelector(selectMonthlyStatsData);
  const monthlyStatsLoading = useSelector(selectMonthlyStatsLoading);
  const monthlyStatsPreviousData = useSelector(selectMonthlyStatsPreviousData);
  const activeUsers = useSelector(selectActiveUsers);
  const blurSensitive = useSelector(selectBlurSensitive);

  // Threshold the engine used for this report (falls back to 5 for records
  // written before max_missed_days became part of the payload).
  const maxMissedDays = monthlyStatsData?.max_missed_days ?? 5;

  // Days scored so far this period. A forecaster missing from the board has
  // no forecast for this horizon yet, which only breaks the allowance once
  // more days than it have been scored. Unknown keeps the allowance wording.
  const scoredDays = monthlyStatsData?.days_in_period;
  const allowancePassed = scoredDays == null || scoredDays > maxMissedDays;
  const horizonName = Number(horizon) === 0 ? 'intraday' : `D+${horizon}`;

  const prevPeriod = useMemo(() => {
    if (year == null || month == null) return null;
    const prev = moment({ year, month: month - 1 }).subtract(1, 'month');
    return { year: prev.year(), month: prev.month() + 1 };
  }, [year, month]);

  // Resolve which userId to pass to the per-user APIs. When the logged-in user
  // has no forecaster account (admin), fall back to any active forecaster so
  // the shared `month_scores_json` payload can still be fetched.
  const referenceUserId = useMemo(() => {
    if (activeUserId) return activeUserId;
    if (Array.isArray(activeUsers) && activeUsers.length > 0) {
      // Prefer a qualified user — their row carries the shared
      // `month_scores_json`. Unqualified rows have it empty/absent.
      const qualified = activeUsers.find((u) => u.has_stats === true);
      const pick = qualified || activeUsers[0];
      return pick.user_id || pick.id || null;
    }
    return null;
  }, [activeUserId, activeUsers]);

  // Fetch active users when we have no logged-in forecaster id (admin fallback)
  // or when admin/owner so we can surface unqualified forecasters too.
  useEffect(() => {
    if (!resourceId) return;
    if (!isAdmin && activeUserId) return;
    dispatch(fetchActiveUsers({ resourceId, year, month, track }));
  }, [dispatch, resourceId, isAdmin, activeUserId, year, month, track]);

  // Fetch monthly stats for the selected horizon.
  useEffect(() => {
    if (!resourceId || !referenceUserId || horizon == null) return;
    dispatch(fetchMonthlyStats({
      userId: referenceUserId,
      resourceId,
      year,
      month,
      track,
      horizonOffset: horizon,
    }));
  }, [dispatch, resourceId, referenceUserId, year, month, track, horizon]);

  // Fetch the previous month's stats in parallel so we can compute MoM deltas.
  useEffect(() => {
    if (!resourceId || !referenceUserId || horizon == null || !prevPeriod) return;
    dispatch(fetchMonthlyStatsPrevious({
      userId: referenceUserId,
      resourceId,
      year: prevPeriod.year,
      month: prevPeriod.month,
      track,
      horizonOffset: horizon,
    }));
  }, [dispatch, resourceId, referenceUserId, prevPeriod, track, horizon]);

  const previousScoreByUser = useMemo(() => {
    const raw = monthlyStatsPreviousData?.month_scores_json;
    if (!Array.isArray(raw)) return {};
    return raw.reduce((acc, row) => {
      if (row && row.user_id != null && row.score != null) {
        acc[row.user_id] = Number(row.score);
      }
      return acc;
    }, {});
  }, [monthlyStatsPreviousData]);

  // Score-sorted board. Forecasters the engine flags `is_unqualified` (over
  // the missed-day limit) keep their position but get no rank slot, so
  // qualified peers keep contiguous numbering (mirrors ErrorsReport and
  // MobileAnalyticsSummary).
  const scores = useMemo(() => {
    const raw = monthlyStatsData?.month_scores_json;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    let qualifiedRank = 0;
    return [...raw]
      .filter((row) => row && row.score != null)
      .sort((a, b) => Number(a.score) - Number(b.score))
      .map((row) => {
        const unqualified = row.is_unqualified === true;
        return { ...row, rank: unqualified ? null : ++qualifiedRank, unqualified };
      });
  }, [monthlyStatsData]);

  // Forecasters missing from a published board. Before 2026-04 the engine
  // dropped unqualified forecasters from `month_scores_json`; since then it
  // lists everyone with a forecast for this horizon, so a missing one has
  // none this month and every scored day is a penalty. With no board at all
  // (null) nothing has been aggregated yet, so nobody reads as missing.
  const boardPublished = Array.isArray(monthlyStatsData?.month_scores_json);

  const loggedInUserEntry = useMemo(
    () => scores.find((row) => row.user_id === activeUserId) || null,
    [scores, activeUserId],
  );

  // When the logged-in forecaster is missing from month_scores_json, surface
  // their own record from monthlyStatsData so they still appear with a warning.
  const excludedSelfEntry = useMemo(() => {
    if (!activeUserId || loggedInUserEntry || !boardPublished) return null;
    if (!monthlyStatsData || monthlyStatsData.user_id !== activeUserId) return null;
    if (monthlyStatsData.avg_score_w_pen == null) return null;
    return {
      user_id: activeUserId,
      score: monthlyStatsData.avg_score_w_pen,
      email: monthlyStatsData.user_email,
      username: monthlyStatsData.user_username,
      n_days_w_penalties: monthlyStatsData.n_days_w_penalties,
      days_with_submissions: monthlyStatsData.days_with_submissions,
      days_in_period: monthlyStatsData.days_in_period,
      unqualified: true,
    };
  }, [activeUserId, loggedInUserEntry, boardPublished, monthlyStatsData]);

  // Active users present in the period but missing from month_scores_json.
  // Surface them as warning rows so admins/owners see the full picture
  // instead of a silently-shorter leaderboard.
  const excludedOtherEntries = useMemo(() => {
    if (!isAdmin || !boardPublished) return [];
    if (!Array.isArray(activeUsers) || activeUsers.length === 0) {
      return [];
    }
    const knownIds = new Set(scores.map((row) => row.user_id));
    if (activeUserId) knownIds.add(activeUserId);
    const seen = new Set();
    return activeUsers
      .filter((u) => {
        const id = u.user_id || u.id;
        if (!id || knownIds.has(id) || seen.has(id)) return false;
        // Only surface users the engine actually scored (has_stats) but then
        // dropped from month_scores_json for exceeding the penalty allowance.
        // Users without a stats row have simply not been evaluated yet (e.g.
        // start of month, before observations arrive): they submitted but
        // carry no score, so labelling them "exceeded N-day allowance" is wrong.
        if (u.has_stats !== true) return false;
        seen.add(id);
        return true;
      })
      .map((u) => ({
        user_id: u.user_id || u.id,
        email: u.user_email || u.email,
        username: u.user_username || u.username,
        score: null,
        unqualified: true,
        noForecastsYet: !allowancePassed,
      }));
  }, [isAdmin, boardPublished, allowancePassed, activeUsers, scores, activeUserId]);

  const rank1Entry = scores.find((row) => row.rank === 1) || null;

  // Delta baseline: logged-in user's score when available (even if excluded);
  // otherwise rank 1 (admins or forecasters with no submissions at all).
  const deltaBaseline = loggedInUserEntry || excludedSelfEntry || rank1Entry;
  const referenceScore = deltaBaseline?.score;
  const deltaVsLabel = (loggedInUserEntry || excludedSelfEntry) ? 'vs you' : 'vs #1';
  const deltaLabel = `Δ ${deltaVsLabel}`;

  const rows = useMemo(
    () => [
      ...scores,
      ...(excludedSelfEntry ? [excludedSelfEntry] : []),
      ...excludedOtherEntries,
    ],
    [scores, excludedSelfEntry, excludedOtherEntries],
  );

  const selfRowIndex = useMemo(
    () => (activeUserId ? rows.findIndex((r) => r.user_id === activeUserId) : -1),
    [rows, activeUserId],
  );

  const pinnedRow = useMemo(
    () => (selfRowIndex >= 0 ? rows[selfRowIndex] : null),
    [rows, selfRowIndex],
  );

  const scrollRef = useRef(null);
  const selfRowRef = useRef(null);
  const [selfInView, setSelfInView] = useState(true);
  // Drives the bottom-fade overlay below: hidden when the user has scrolled
  // to the end so the last row isn't gratuitously faded. The fade signals
  // "more content below" — the subtle scrollbar alone is too easy to miss.
  const [atScrollEnd, setAtScrollEnd] = useState(false);
  // Phone-only: top 8 rows show by default, this reveals the rest.
  const [showAllPhone, setShowAllPhone] = useState(false);

  const attachSelfRowRef = useCallback((node) => {
    selfRowRef.current = node;
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    const target = selfRowRef.current;
    if (!container || !target) {
      setSelfInView(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setSelfInView(entry.isIntersecting),
      { root: container, threshold: 0.5 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [rows, selfRowIndex]);

  // Track whether the user is scrolled to the bottom. Used to hide the
  // bottom-fade overlay so the final row doesn't appear washed out at rest.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return undefined;
    const update = () => {
      const remaining =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      // Tolerance of 4px covers sub-pixel rounding on high-DPI displays.
      setAtScrollEnd(remaining <= 4);
    };
    update();
    container.addEventListener('scroll', update, { passive: true });
    // Re-evaluate on row-count change (container height may now exceed
    // viewport, or not).
    return () => container.removeEventListener('scroll', update);
  }, [rows]);

  // Keep previous content visible while a refetch is in flight to avoid
  // layout shifts; only show the spinner on the initial (no-data) load.
  const isLoading = monthlyStatsLoading && !monthlyStatsData;

  const header = (
    <CardHeader
      title="Leaderboard"
      description="Forecasters ranked by average score for the selected period. Lower is better."
      className="mb-3"
    />
  );

  const renderEmptyState = () => (
    <div className="flex-1 flex items-center justify-center py-6 text-center text-xs text-gray-400 dark:text-gray-500">
      No data for the selected period.
    </div>
  );

  const renderTable = () => {
    if (
      scores.length === 0 &&
      !excludedSelfEntry &&
      excludedOtherEntries.length === 0
    ) {
      return renderEmptyState();
    }

    const showDelta = rows.length > 1 && referenceScore != null;
    const showMomDelta = Object.keys(previousScoreByUser).length > 0;

    const renderRow = (row, { refSelf = false, index } = {}) => {
      const isSelf = row.user_id === activeUserId;
      const isBaseline = deltaBaseline && row.user_id === deltaBaseline.user_id;
      const isUnqualified = row.unqualified === true;
      const display = blurSensitive
        ? (row.username || row.user_id)
        : (row.email || row.username || row.user_id);

      let deltaDisplay = '—';
      let deltaClass = 'text-gray-400 dark:text-gray-500';
      if (showDelta) {
        if (isBaseline || isUnqualified || row.score == null) {
          deltaDisplay = '—';
        } else {
          const diff = Number(row.score) - Number(referenceScore);
          deltaDisplay = formatDelta(diff);
          if (Math.abs(diff) < 0.01) {
            deltaClass = 'text-gray-400 dark:text-gray-500';
          } else if (diff < 0) {
            deltaClass = 'text-green-600 dark:text-green-400';
          } else {
            deltaClass = 'text-red-500 dark:text-red-400';
          }
        }
      }

      const rowClass = isSelf
        ? 'border-l-2 border-brand-500 bg-brand-50 dark:bg-brand-900/20'
        : 'border-l-2 border-transparent';

      const penaltyDays = row.n_days_w_penalties;
      const noForecastsYet = row.noForecastsYet === true;
      const warningTooltip = isUnqualified
        ? (penaltyDays != null
          ? `Not evaluated this period — ${penaltyDays} days with penalties exceed the ${maxMissedDays}-day limit.`
          : noForecastsYet
            ? `Not evaluated this period: no ${horizonName} forecasts yet.`
            : `Not evaluated this period — exceeded the ${maxMissedDays}-day penalty allowance.`)
        : null;

      const isClickable = Boolean(onForecasterClick) && row.user_id != null;

      return (
        <tr
          key={row.user_id || `${row.rank}-${display}`}
          ref={refSelf ? attachSelfRowRef : undefined}
          onClick={isClickable ? () => onForecasterClick(row.user_id) : undefined}
          className={`${rowClass} ${isUnqualified ? 'opacity-80' : ''} ${isClickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60' : ''} ${index >= 8 && !showAllPhone ? 'hidden lg:table-row' : ''}`}
        >
          <td className="py-3 lg:py-2 pr-2 pl-2 text-sm lg:text-xs font-mono font-semibold text-gray-500 dark:text-gray-400 tabular-nums w-10">
            {isUnqualified ? (
              <span title={warningTooltip} className="text-amber-500 dark:text-amber-400">—</span>
            ) : (
              row.rank
            )}
          </td>
          <td className="py-3 lg:py-2 px-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <div
                  className={`truncate text-sm lg:text-xs font-medium ${isSelf ? 'text-brand-700 dark:text-brand-300' : 'text-gray-900 dark:text-gray-100'}`}
                  title={display}
                >
                  {display}
                </div>
                {isUnqualified && (
                  <ExclamationTriangleIcon
                    className="h-3.5 w-3.5 flex-none text-amber-500 dark:text-amber-400"
                    title={warningTooltip}
                  />
                )}
              </div>
              {/* Phone-only: from sm the delta column carries the value. The
                  baseline row has no delta, so it reads "Top score"; a row
                  without a score gets no sub-line. */}
              {showDelta && !isUnqualified && (isSelf || isBaseline || row.score != null) && (
                <div className={`sm:hidden mt-0.5 text-xs ${isSelf ? 'text-brand-700 dark:text-brand-300' : isBaseline ? 'text-gray-500 dark:text-gray-400' : deltaClass}`}>
                  {isSelf ? 'Your score' : isBaseline ? 'Top score' : `${deltaDisplay} ${deltaVsLabel}`}
                </div>
              )}
              {isUnqualified && (
                <div
                  className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 leading-tight"
                  title={warningTooltip}
                >
                  {penaltyDays != null
                    ? `Not evaluated · ${penaltyDays} penalty day${penaltyDays === 1 ? '' : 's'} (max ${maxMissedDays})`
                    : noForecastsYet
                      ? `Not evaluated · no ${horizonName} forecasts yet`
                      : `Not evaluated · exceeded ${maxMissedDays}-day allowance`}
                </div>
              )}
            </div>
          </td>
          <td className={`py-3 lg:py-2 px-2 text-right text-sm lg:text-xs font-mono tabular-nums w-24 ${isUnqualified ? 'text-gray-400 dark:text-gray-500 italic' : 'text-gray-700 dark:text-gray-200'}`}>
            {formatScore(row.score)}
          </td>
          {showDelta && (
            <td className={`hidden sm:table-cell py-2 pl-2 pr-2 text-right text-xs font-mono font-semibold tabular-nums w-24 ${deltaClass}`}>
              {deltaDisplay}
            </td>
          )}
          {showMomDelta && (() => {
            const prevScore = previousScoreByUser[row.user_id];
            if (prevScore == null || row.score == null) {
              return (
                <td className="hidden sm:table-cell py-2 pl-2 pr-2 text-right text-xs font-mono tabular-nums text-gray-400 dark:text-gray-500 w-24">
                  —
                </td>
              );
            }
            const momDiff = Number(row.score) - Number(prevScore);
            let momClass = 'text-gray-400 dark:text-gray-500';
            if (Math.abs(momDiff) >= 0.01) {
              momClass = momDiff < 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-500 dark:text-red-400';
            }
            return (
              <td
                className={`hidden sm:table-cell py-2 pl-2 pr-2 text-right text-xs font-mono font-semibold tabular-nums w-24 ${momClass}`}
                title={`Previous: ${formatScore(prevScore)}`}
              >
                {formatDelta(momDiff)}
              </td>
            );
          })()}
        </tr>
      );
    };

    const headerBg = 'bg-surface dark:bg-gray-870';
    const showPinned = !selfInView && pinnedRow != null;

    return (
      <div className="flex flex-col flex-1 min-h-0">
        <div className="relative">
          {/* Scrolls sideways at every width so the sm+ delta columns stay in
              a half-width card; the 360px vertical scroll area is lg only. */}
          <div ref={scrollRef} className="subtle-scrollbar overflow-x-auto lg:overflow-auto lg:max-h-[360px]">
            <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500">
              <tr>
                <th className={`sticky top-0 z-10 ${headerBg} border-b border-gray-100 dark:border-gray-700 py-2 pr-2 pl-2 text-left font-medium w-10`}>Rank</th>
                <th className={`sticky top-0 z-10 ${headerBg} border-b border-gray-100 dark:border-gray-700 py-2 px-2 text-left font-medium`}>Forecaster</th>
                <th className={`sticky top-0 z-10 ${headerBg} border-b border-gray-100 dark:border-gray-700 py-2 px-2 text-right font-medium w-24`}>Score</th>
                {showDelta && (
                  <th className={`hidden sm:table-cell sticky top-0 z-10 ${headerBg} border-b border-gray-100 dark:border-gray-700 py-2 pl-2 pr-2 text-right font-medium w-24`}>{deltaLabel}</th>
                )}
                {showMomDelta && (
                  <th
                    className={`hidden sm:table-cell sticky top-0 z-10 ${headerBg} border-b border-gray-100 dark:border-gray-700 py-2 pl-2 pr-2 text-right font-medium w-24`}
                    title="Change vs. previous month"
                  >
                    Δ vs prev.
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) =>
                renderRow(row, { refSelf: idx === selfRowIndex, index: idx }),
              )}
            </tbody>
          </table>
          </div>
          {/* Bottom fade — universal "more below" signal so users don't
              miss the subtle scrollbar. Hides itself when scrolled to the
              end so the final row isn't gratuitously washed out. */}
          <div
            aria-hidden="true"
            className={`hidden lg:block pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-surface dark:from-gray-870 to-transparent transition-opacity duration-200 ${atScrollEnd ? 'opacity-0' : 'opacity-100'}`}
          />
        </div>
        {/* Pinned self row sits directly under the list: on a phone the
            truncated top-8 view hides the viewer's own row, and rendering the
            pin below the "Show all N" toggle made it read as though N-8
            forecasters stood between them and the last visible rank. */}
        {showPinned && (
          <div
            className="mt-1 pt-1 border-t-2 border-brand-200 dark:border-brand-700/60 shadow-[0_-8px_12px_-8px_rgba(5,120,138,0.22)]"
            title="Your position"
          >
            <table className="w-full text-sm">
              <tbody>{renderRow(pinnedRow)}</tbody>
            </table>
          </div>
        )}
        {rows.length > 8 && (
          <button type="button" onClick={() => setShowAllPhone(!showAllPhone)} className="lg:hidden mt-1 h-11 w-full rounded-lg text-sm font-medium text-brand-600 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-brand-400 dark:hover:bg-gray-800">
            {showAllPhone ? 'Show top 8' : `Show all ${rows.length} forecasters`}
          </button>
        )}
      </div>
    );
  };

  const totalForecasters = rows.length;
  const unqualifiedCount = rows.filter(
    (row) => row.unqualified === true && !row.noForecastsYet,
  ).length;
  const footer = (
    <div className="pt-3 mt-2 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-500 dark:text-gray-400">
      <span>
        {totalForecasters} forecaster{totalForecasters === 1 ? '' : 's'}
        {unqualifiedCount > 0 && ` · ${unqualifiedCount} unqualified`}
      </span>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col">
      {header}

      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <AnimationCircle />
        </div>
      ) : horizon == null ? (
        renderEmptyState()
      ) : (
        renderTable()
      )}

      {totalForecasters > 0 && footer}
    </div>
  );
}
